import { useTranslation } from 'react-i18next';

import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { HostingLicenseModel } from '@newfold/huapi-js/src/index.schemas';

import CTB from '~/components/CTB';
import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import { useMFEContext } from '~/components/MFEProvider';
import { productTypes } from '~/hooks/useCtbProductInfo';
import useResponsive from '~/hooks/useResponsive';

import { LICENSE_BLOCKED } from '../../../utils/constants';

interface LicenseCardProps {
  license: HostingLicenseModel | null;
  appName: string;
  isLoading: boolean;
  appLogo: React.ReactNode;
}

const LicenseCard = ({
  license,
  appName,
  isLoading,
  appLogo,
}: LicenseCardProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'licenses',
  });
  const {
    //@ts-expect-error
    hostingId,
  } = useMFEContext();

  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  const hasLicense = license != null;

  const {
    is_upgradeable: isUpgradable,
    status,
    name,
    back_reference: licenseBackReference,
    details,
  } = license || {};

  const shouldShowActionButton = !hasLicense || isUpgradable;

  let ctbProductName = '';
  if (appName.toLowerCase() === 'cpanel') {
    ctbProductName = hasLicense
      ? productTypes.CPANEL_UPGRADE
      : productTypes.CPANEL_FAMILY;
  }

  const showLicenseStatusChip =
    status === 'active' || (status != null && LICENSE_BLOCKED.includes(status));

  return (
    <Card data-testid={`license-card-${appName}`}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={3}
      >
        <Stack spacing={3}>
          {appLogo}
          {name && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4" color="text.secondary">
                {t(`${appName}.licenseType`, { name })}
              </Typography>
              {showLicenseStatusChip ? (
                <Chip
                  label={t(
                    `status.${status === 'active' ? 'active' : 'blocked'}`,
                  )}
                  size="small"
                  color={status === 'active' ? 'success' : 'error'}
                  data-testid={`${appName}-license-status-chip`}
                />
              ) : (
                <Skeleton
                  width={52}
                  height={26}
                  data-testid={`${appName}-license-status-skeleton`}
                />
              )}
            </Stack>
          )}
          {details?.seats != null && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h4" color="text.secondary">
                {t(`${appName}.licenseLimits`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`${appName}.seats`, { count: details?.seats })}
              </Typography>
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary" maxWidth="640px">
            {t(`${appName}.description`)}
          </Typography>
        </Stack>
        {shouldShowActionButton && ctbProductName && (
          // CTB needs to be wrapped in a Stack to prevent Upgrade in progress getting hidden in tablet view
          <Stack>
            <CTB
              product={ctbProductName}
              data={{
                prodInstId: hasLicense ? licenseBackReference : hostingId,
              }}
            >
              {/* @ts-expect-error */}
              <LoadingButtonWrapper
                variant="outlined"
                fullWidth={isMobileView}
                loading={isLoading}
                data-testid={`${appName}-ctb-btn`}
              >
                {hasLicense ? t('action.upgrade') : t('action.getLicense')}
              </LoadingButtonWrapper>
            </CTB>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default LicenseCard;
